import React, { useMemo } from 'react';
import { Text, Box } from "@mantine/core";
import { Card } from "@xplatform/design-system";
import { DonutChart } from '@mantine/charts';
import type { CostBreakdownViewModel } from '@models/cost';
import './CostTypeBreakdownChart.scss';

const COLORS = [
    'brand.5', 'brand.7', 'gray.6', 'teal.5', 'cyan.5',
    'blue.5', 'green.5', 'orange.5', 'brand.3', 'gray.3',
];

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(value);

interface CostTypeBreakdownChartProps {
    data: CostBreakdownViewModel | null;
    isLoading?: boolean;
}

const CostTypeBreakdownChart: React.FC<CostTypeBreakdownChartProps> = ({ data, isLoading }) => {
    const chartData = useMemo(() => {
        if (!data?.breakdown) return [];
        return data.breakdown
            .filter((item) => Number(item.totalCost) > 0)
            .map((item, index) => ({
                name: item.dimensionValue,
                value: Number(item.totalCost),
                color: COLORS[index % COLORS.length],
            }));
    }, [data]);

    if (isLoading) {
        return (
            <Card className="cost-type-breakdown-chart">
                <Text fw={600} size="lg" mb="md">Cost Type Breakdown</Text>
                <Box className="cost-type-breakdown-chart__loading">
                    <Text c="dimmed">Loading...</Text>
                </Box>
            </Card>
        );
    }

    if (chartData.length === 0) {
        return (
            <Card className="cost-type-breakdown-chart">
                <Text fw={600} size="lg" mb="md">Cost Type Breakdown</Text>
                <Box className="cost-type-breakdown-chart__empty">
                    <Text c="dimmed">No cost type data to display.</Text>
                </Box>
            </Card>
        );
    }

    const total = data?.totalCost ? Number(data.totalCost) : 0;

    return (
        <Card className="cost-type-breakdown-chart">
            <Text fw={600} size="lg" mb="md">Cost Type Breakdown</Text>
            <Box className="cost-type-breakdown-chart__content">
                <DonutChart
                    data={chartData}
                    valueFormatter={formatCurrency}
                    withLabelsLine
                    withLabels
                    size={220}
                    thickness={40}
                />
                <Box className="cost-type-breakdown-chart__total" mt="sm">
                    <Text size="sm">
                        Total Cost: <strong>{formatCurrency(total)}</strong>
                    </Text>
                </Box>
            </Box>
        </Card>
    );
};

export default CostTypeBreakdownChart;
